/**
 * Settings → Integrations' API (slice 3b spec §5, §12): what's connected
 * (`GET /api/integrations`), Browse apps' catalog (`GET …/catalog`), and the
 * owner's changes. Both reads sit under `integrationsKey`, which the
 * `integrations` hub event refetches (`app/events.ts`); every change
 * refetches them too. Each person's list is their own accounts and servers
 * plus the shared ones, each with whose it is (slice 6a §5.4).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, API_URL } from "../../app/api";
import { integrationsKey, toolsKey } from "./api";
import { clockSkew, connectTarget } from "./integrations-view";
import type { SignInTab } from "./signin-tab";

export type Level = "read" | "write" | "full";

/** Who connected an account or added a server (slice 6a §5.4). */
export type Connector = { id: string; name: string };

/** An app's level as the connect window shows it: its name here, what it allows, a few words. */
export type LevelInfo = { level: Level; name: string; allows: string; brief: string };

export type Account = {
  id: string;
  app: string;
  name: string | null;
  email: string | null;
  /** The name, else the email; null while a connect waits on Google. */
  label: string | null;
  level: Level;
  primary: boolean;
  status: "connecting" | "active" | "expired" | "disconnecting";
  /** A Reconnect is waiting on Google; the account keeps working meanwhile. */
  reconnecting: boolean;
  connected_at: string | null;
  expired_at: string | null;
  created_at: string;
  /** When a connect or Reconnect stops waiting on its sign-in link, on the server's clock. */
  expires_at?: string | null;
  /** Who connected it; only they change it (slice 6a §5.4). */
  user: Connector;
  /** Anyone in the crew can ask with it, up to its access. */
  shared: boolean;
};

/**
 * Who an app's person signs in with ("Google"), where Crew's access is
 * managed there, and whether Disconnect signs Crew out there itself.
 */
export type Provider = { name: string; access: string; revokes: boolean };

export type IntegrationApp = {
  app: string;
  name: string;
  mark: string;
  /** Its drawn icon (`app-icons.ts`); none shows the mark. */
  icon?: string;
  covers: string;
  note?: string;
  /** What the provider will show, at every level (spec §2). */
  consent: string;
  provider: Provider;
  levels: LevelInfo[];
  /** Another account can be connected: under the limit, a connect still waiting counted. */
  can_add: boolean;
  accounts: Account[];
};

export type McpAuth = "none" | "api_key" | "oauth";

export type McpServer = {
  id: string;
  name: string;
  url: string;
  auth: McpAuth;
  status: "connecting" | "active" | "expired" | "error";
  error?: string;
  directory_id?: string;
  /** When its tools were last read; null before the first read. */
  synced_at: string | null;
  /** Remove started and didn't finish: unusable, and only Remove again is offered. */
  removing?: boolean;
  tools: { on: number; off: number };
  /** Its guides: one changed and waiting, or one Crew couldn't take (slice 6k §5). */
  guides?: { changed: boolean; unreadable: boolean };
  /** When an OAuth server still being added stops waiting on its sign-in link. */
  expires_at?: string | null;
  /** Who added it; only they change it (slice 6a §5.4). */
  user: Connector;
  /** Anyone in the crew can ask with its tools that are On. */
  shared: boolean;
};

/** One of a server's tools (spec §6.1): On or Off, and what it says about itself. */
export type McpTool = {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  read_only: boolean;
  destructive: boolean;
};

/** A server's guide, as its tools window lists it (slice 6k §5). */
export type McpGuide = {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  /** A new version is waiting; the crew keeps this one until it's accepted. */
  changed: boolean;
};

export type GuideFile = { path: string; text: string };

/** A guide's text: what's in use, and the new version when one is waiting. */
export type McpGuideText = McpGuide & {
  files: GuideFile[];
  pending?: { digest: string; description: string; files: GuideFile[] };
};

export type Integrations = {
  available: boolean;
  /** The server's clock when it answered: a sign-in's countdown runs on it. */
  now?: string;
  /** Not the API's: the server's clock less this browser's (`useIntegrations`). */
  skew?: number;
  apps: IntegrationApp[];
  mcp_servers: McpServer[];
};

export type CatalogApp = {
  app: string;
  name: string;
  category: string;
  category_name: string;
  blurb: string;
  mark: string;
  icon?: string;
  consent: string;
  provider: Provider;
  levels: LevelInfo[];
  /** How many accounts the workspace has for it (a connect in progress isn't one). */
  accounts: number;
  /** Another account can be connected (the server's limit counts a connect in progress). */
  can_add: boolean;
};

export type DirectoryEntry = {
  id: string;
  name: string;
  blurb: string;
  domain: string;
  url: string;
  auth: "oauth" | "api_key" | "none" | "unknown";
  checked: boolean;
  /** An API key's header and format, when the directory lists them. */
  header?: string;
  format?: string;
  /** Its logo, a static file Crew keeps (`mcp-logos.ts`); none shows two letters. */
  logo?: string;
  /** The workspace already has a server from this entry. */
  added: boolean;
};

export type Catalog = { available: boolean; apps: CatalogApp[]; directory: DirectoryEntry[] };

export const catalogKey = [...integrationsKey, "catalog"] as const;

/**
 * What's connected, with `skew`: the server's clock less this browser's,
 * as of the answer's arrival (`clockSkew`), for a sign-in's countdown.
 */
export const useIntegrations = () =>
  useQuery({
    queryKey: integrationsKey,
    queryFn: async () => {
      const list = await api<Integrations>("GET", "/api/integrations");
      return { ...list, skew: clockSkew(list.now, Date.now()) };
    },
    staleTime: 0,
  });

export const useCatalog = () =>
  useQuery({
    queryKey: catalogKey,
    queryFn: () => api<Catalog>("GET", "/api/integrations/catalog"),
    staleTime: 0,
  });

/** Refetches Integrations, Browse apps and Tools' "Your apps" once a change settles. */
function useChange<T, R>(run: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: integrationsKey }),
        client.invalidateQueries({ queryKey: toolsKey }),
      ]);
    },
  });
}

/** What a connect remembers across Google's sign-in, so the page it comes back to can name it. */
/** What a tab remembers of a connect it started: `provider` names who it signs in with. */
export type Started = {
  app: string;
  name: string;
  provider?: string;
  account_id?: string;
  label?: string;
};

const STARTED = "crew.integrations.started";

export function rememberStarted(started: Started) {
  try {
    sessionStorage.setItem(STARTED, JSON.stringify(started));
  } catch {
    /* private mode: the banner says less */
  }
}

/** The connect this tab started, if any. */
export function readStarted(): Started | null {
  try {
    const raw = sessionStorage.getItem(STARTED);
    return raw ? (JSON.parse(raw) as Started) : null;
  } catch {
    return null;
  }
}

/** Forgets it once the page it came back to has read it. */
export function clearStarted() {
  try {
    sessionStorage.removeItem(STARTED);
  } catch {
    /* nothing kept */
  }
}

/** `tab`: the sign-in's own tab, opened in the click (`openSignInTab`); null goes in this one. */
type ConnectInput = (
  | { app: string; level: Level; started: Started }
  | { app: string; account_id: string; started: Started }
) & { tab: SignInTab | null };

type ConnectBody = { app: string; level: Level } | { app: string; account_id: string };

/** `DELETE /api/integrations/connect/:id`'s answer: null (`204`), or the cleanup still pending (`202`). */
type Cancelled = { cleanup?: string } | null;

/**
 * `POST /api/integrations/connect` → `{ url, account_id, link }`, then the
 * browser goes there: Google's sign-in through Composio (or mock mode's
 * consent page). A link it won't open (`connectTarget`) is undone first,
 * named by its own `link` token, so a newer Reconnect's link is never the
 * one cancelled (Codex and Devin on #247). If Composio couldn't release it
 * yet (or the cancel failed), the page doesn't say it's done: it's kept for
 * the sweep, and the words are neutral.
 */
export async function openConnect(
  body: ConnectBody,
  deps: {
    post: (body: ConnectBody) => Promise<{ url: string; account_id: string; link: string }>;
    cancel: (accountId: string, link: string) => Promise<Cancelled>;
    assign: (url: string) => void;
    apiOrigin: string;
  },
): Promise<string> {
  const { url, account_id, link } = await deps.post(body);
  const target = connectTarget(url, deps.apiOrigin);
  if (target) {
    deps.assign(target);
    return target;
  }
  const cancelled = await deps.cancel(account_id, link).catch(() => ({ cleanup: "pending" }));
  if (cancelled?.cleanup === "pending") {
    throw new ApiError(0, "cleanup_pending", "Couldn't open the sign-in. Try again in a minute.");
  }
  throw new ApiError(
    0,
    "bad_link",
    "That sign-in link didn't look right, so Crew didn't open it. Try again.",
  );
}

/** Sends a sign-in to its link: in its own tab when there is one, else this tab leaves for it. */
function go(target: string, tab: SignInTab | null) {
  if (tab) tab.go(target);
  else window.location.assign(target);
}

/**
 * Connect or Reconnect (spec §5.2, §5.3). In its own tab, this page stays:
 * the list refetches to show the row waiting on its sign-in, and the tab
 * tells this one how it went (`signin-tab.ts`). A refusal closes the tab.
 */
export function useConnect() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ started, tab, ...body }: ConnectInput) => {
      try {
        const target = await openConnect(body, {
          post: (b) =>
            api<{ url: string; account_id: string; link: string }>(
              "POST",
              "/api/integrations/connect",
              b,
            ),
          cancel: (id, link) =>
            api<Cancelled>(
              "DELETE",
              `/api/integrations/connect/${id}?link=${encodeURIComponent(link)}`,
            ),
          assign: (url) => {
            // In its own tab, the connect is kept by that tab's id (`signin-tab.ts`).
            if (!tab) rememberStarted(started);
            go(url, tab);
          },
          apiOrigin: API_URL || window.location.origin,
        });
        return { target, inTab: tab !== null };
      } catch (error) {
        tab?.close();
        throw error;
      }
    },
    onSuccess: (out) => {
      if (out.inTab) void client.invalidateQueries({ queryKey: integrationsKey });
    },
    onError: () => client.invalidateQueries({ queryKey: integrationsKey }),
  });
}

export type AccountChange = {
  name?: string | null;
  primary?: true;
  level?: Level;
  shared?: boolean;
};

/**
 * `PATCH /api/integrations/accounts/:id`: Rename, Make primary, Change access
 * (spec §5.3), and Share or Just you (slice 6a §5.4).
 */
export const useChangeAccount = () =>
  useChange(({ id, ...change }: AccountChange & { id: string }) =>
    api<{ account: Account }>("PATCH", `/api/integrations/accounts/${id}`, change),
  );

/**
 * `DELETE`: `204` once revoked and deleted (null here); `200 { revoked: false }`
 * when it's deleted but Google wasn't asked to revoke it (spec §5.4);
 * `202 { account }` when a revoke failed.
 */
export const useDisconnectAccount = () =>
  useChange((id: string) =>
    api<{ revoked?: boolean; account?: Account } | null>(
      "DELETE",
      `/api/integrations/accounts/${id}`,
    ),
  );

/** Try again after a failed revoke (spec §5.4). */
export const useRetryDisconnect = () =>
  useChange((id: string) =>
    api<{ revoked?: boolean; account?: Account } | null>(
      "POST",
      `/api/integrations/accounts/${id}/disconnect/retry`,
    ),
  );

// ── MCP servers (spec §6, §12) ────────────────────────────────────────────

const serverToolsKey = (id: string) => [...integrationsKey, "mcp", id, "tools"] as const;

type ServerTools = { server: McpServer; tools: McpTool[]; guides?: McpGuide[] };

/** A server's tools, for its list (Paper 43). */
export const useServerTools = (id: string) =>
  useQuery({
    queryKey: serverToolsKey(id),
    queryFn: () => api<ServerTools>("GET", `/api/integrations/mcp/${id}/tools`),
    staleTime: 0,
  });

/**
 * Sends an OAuth server's sign-in to its link, in its own tab or this one,
 * when the link is one the page will open (`connectTarget`); else it says so
 * and the tab closes. The server stays `connecting`, and the page's load
 * tidies it after 10 minutes.
 */
function signIn(url: string, tab: SignInTab | null) {
  const target = connectTarget(url, API_URL || window.location.origin);
  if (!target) {
    tab?.close();
    throw new ApiError(
      0,
      "bad_link",
      "That sign-in link didn't look right, so Crew didn't open it. Remove the server and try again.",
    );
  }
  go(target, tab);
}

type AddedServer = { server: McpServer; url?: string };

/** What a sign-in left: the server's answer, and whether it went to a tab of its own. */
type SignedIn = AddedServer & { inTab: boolean };

/** Runs a call that may return a sign-in link; the tab closes when there's none or it fails. */
async function withSignIn(
  tab: SignInTab | null | undefined,
  call: () => Promise<AddedServer>,
  send: (out: AddedServer) => boolean = () => true,
): Promise<SignedIn> {
  try {
    const out = await call();
    if (out.url && send(out)) {
      signIn(out.url, tab ?? null);
      return { ...out, inTab: !!tab };
    }
    tab?.close();
    return { ...out, inTab: false };
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/** `POST /api/integrations/mcp`: added, or for OAuth, off to sign in (in `tab`, opened in the click). */
export const useAddServer = () =>
  useChange(({ body, tab }: { body: Record<string, unknown>; tab?: SignInTab | null }) =>
    withSignIn(tab, () => api<AddedServer>("POST", "/api/integrations/mcp", body)),
  );

/** `PATCH …/tools/:tool_id { enabled }`, shown at once and put back if it fails. */
export function useSetTool(serverId: string) {
  const client = useQueryClient();
  const key = serverToolsKey(serverId);
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api<{ tool: McpTool }>("PATCH", `/api/integrations/mcp/${serverId}/tools/${id}`, { enabled }),
    onMutate: async ({ id, enabled }) => {
      await client.cancelQueries({ queryKey: key });
      const before = client.getQueryData<ServerTools>(key);
      if (before) {
        client.setQueryData<ServerTools>(key, {
          ...before,
          tools: before.tools.map((t) => (t.id === id ? { ...t, enabled } : t)),
        });
      }
      return { before };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.before) client.setQueryData(key, ctx.before);
    },
    onSettled: () => client.invalidateQueries({ queryKey: integrationsKey }),
  });
}

/** The shortcuts: Turn all on, Read-only tools only (spec §6.1). */
export const useSetTools = (serverId: string) =>
  useChange((tools: "all" | "read_only") =>
    api<ServerTools>("PATCH", `/api/integrations/mcp/${serverId}`, { tools }),
  );

/** Share or Just you for a server (slice 6a §5.4). */
export const useShareServer = () =>
  useChange(({ id, shared }: { id: string; shared: boolean }) =>
    api<ServerTools>("PATCH", `/api/integrations/mcp/${id}`, { shared }),
  );

/** Refresh tools: `502 mcp_unreachable` leaves the server `error` with why. */
/** A guide's text, to read (slice 6k §5). */
export const useGuideText = (serverId: string, guideId: string) =>
  useQuery({
    queryKey: [...serverToolsKey(serverId), "guide", guideId],
    queryFn: () =>
      api<{ guide: McpGuideText }>("GET", `/api/integrations/mcp/${serverId}/guides/${guideId}`),
    staleTime: 0,
  });

/** A guide On or Off, or the change the person just read accepted. */
export function useChangeGuide(serverId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...change }: { id: string; enabled?: boolean; accept_digest?: string }) =>
      api<{ guide: McpGuide }>("PATCH", `/api/integrations/mcp/${serverId}/guides/${id}`, change),
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: serverToolsKey(serverId) }),
        client.invalidateQueries({ queryKey: integrationsKey }),
      ]);
    },
  });
}

export const useRefreshServer = () =>
  useChange((id: string) =>
    api<{ server: McpServer }>("POST", `/api/integrations/mcp/${id}/refresh`),
  );

/**
 * Reconnect (or Try again): a new key, or off to sign in again (in `tab`,
 * opened in the click), only while `current` still says so (the card is
 * still shown: not removed meanwhile).
 */
export const useReconnectServer = () =>
  useChange(
    ({
      id,
      api_key,
      current,
      tab,
    }: {
      id: string;
      api_key?: string;
      current?: () => boolean;
      tab?: SignInTab | null;
    }) =>
      withSignIn(
        tab,
        () =>
          api<AddedServer>(
            "POST",
            `/api/integrations/mcp/${id}/reconnect`,
            api_key ? { api_key } : {},
          ),
        () => current?.() ?? true,
      ),
  );

/** Remove: Composio forgets it, then Crew does. */
export const useRemoveServer = () =>
  useChange((id: string) => api<null>("DELETE", `/api/integrations/mcp/${id}`));
